import { Button } from '@vrplatform/components';
import { useApiQuery } from '@vrplatform/data';
import { InfiniteTable, type MRT_SortingState } from '@vrplatform/table';
import { Avatar, Box, Group, Modal, Title } from '@mantine/core';
import { Text } from '@mantine/core';
import type { ReservationsFilterParams } from '@vrplatform/ui-common';
import { useEffect, useState } from 'react';
import { type StoreApi, createStore, useStore } from 'zustand';
import { type TableSelectionSlice, createTableSelectionSlice } from '../stores';
import {
  TableStoreProvider,
  useTableStoreSelected,
  useTableStoreSetFilter,
  useTableStoreSetSelected,
} from '../stores';
import { ReservationsTableFilter } from '../views/reservations/ledger/ReservationsTableFilter';
import { useReservationsTableColumns } from '../views/reservations/ledger/useReservationsTableColumns';
import { useReservationsTableFilter } from '../views/reservations/ledger/useReservationsTableFilter';
import {
  type ReservationRow,
  useReservationsTableQuery,
} from '../views/reservations/ledger/useReservationsTableQuery';

const useReservationQuery = (props: { reservationId: string | null }) => {
  return useApiQuery(
    async (api, signal, args, hasura) => {
      if (!args.reservationId) return null;

      const [reservation, apps] = await Promise.all([
        api
          .GET('/reservations/{id}', {
            params: {
              path: {
                id: args.reservationId,
              },
            },
            signal,
          })
          .then((res) => {
            if (res.error) throw res.error;

            return res.data;
          }),
        hasura(
          (q) => {
            return q.app({}).map((app) => ({
              id: app.id,
              name: app.name,
              icon: app.icon,
            }));
          },
          {
            fetchOptions: {
              signal,
            },
          }
        ),
      ]);

      return {
        reservation: {
          ...reservation,
          connection: {
            ...reservation.connection,
            app: apps.find((app) => app.id === reservation.connection?.appId),
          },
        },
      };
    },
    {
      queryGroup: 'reservations',
      uniqueQueryKey: 'select-reservation-modal',
      variables: {
        reservationId: props.reservationId,
      },
      queryKeys: ['reservations'],
      keepPreviousData: true,
    }
  );
};

type Props = {
  opened: boolean;
  closeModal: () => void;
  activeReservationId: string | null;
  handleReservationIdSelect: (id: string) => Promise<void> | void;
  title: string;
  subtitle: string;
  submitLabel: string;
  filter?: ReservationsFilterParams;
  withOverlay?: boolean;
  excludedReservationIds?: string[];
};

type SelectedReservationRow = ReservationRow & {
  index: number;
};

type StoreProps = TableSelectionSlice & {
  reservation: SelectedReservationRow | null;
  setReservation: (reservation: SelectedReservationRow | null) => void;
  getReservation: () => SelectedReservationRow | null;
};

export const SelectReservationModal = (props: Props) => (
  <TableStoreProvider filterType="state">
    <SelectModal {...props} />
  </TableStoreProvider>
);

const SelectModal = ({
  opened,
  closeModal,
  activeReservationId,
  handleReservationIdSelect,
  submitLabel,
  subtitle,
  title,
  filter: defaultFilter,
  withOverlay = true,
  excludedReservationIds,
}: Props) => {
  const { data } = useReservationQuery({
    reservationId: activeReservationId,
  });

  const initialReservation = data?.reservation;

  const [store] = useState(
    createStore<StoreProps>((set, get, store) => ({
      ...createTableSelectionSlice(set, get, store),
      reservation: null,
      setReservation: (reservation: SelectedReservationRow | null) =>
        set((state) => ({
          ...state,
          reservation,
        })),
      getReservation: () => get().reservation,
    }))
  );

  const getReservation = useStore(store, (st) => st.getReservation);
  const setReservation = useStore(store, (st) => st.setReservation);
  const setSelected = useStore(store, (st) => st.setSelected);

  const setFilter = useTableStoreSetFilter();

  useEffect(() => {
    const current = getReservation();

    if (!current && initialReservation && activeReservationId) {
      setReservation({
        ...(initialReservation as any),
        index: 0,
      });
      setSelected({ [initialReservation.id]: true });
    }
  }, [
    activeReservationId,
    initialReservation,
    getReservation,
    setReservation,
    setSelected,
  ]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: defaultFilter effect
  useEffect(() => {
    if (defaultFilter && !opened) {
      setTimeout(() => {
        setFilter({
          ...defaultFilter,
          date: defaultFilter.date ? defaultFilter.date.join('...') : undefined,
        });
      }, 500);
    }
  }, [defaultFilter, opened]);

  return (
    <Modal
      centered
      withinPortal
      opened={opened}
      onClose={closeModal}
      overlayProps={
        withOverlay
          ? undefined
          : {
              opacity: 0,
            }
      }
      title={
        <Box>
          <Title order={5}>{title}</Title>
          <Text span c="gray" size="xs">
            {subtitle}
          </Text>
        </Box>
      }
      size={1300}
      zIndex={150}
      styles={{
        title: {
          fontWeight: 500,
        },
        body: {
          display: 'flex',
          flexDirection: 'column',
          paddingBottom: 0,
          paddingTop: 2,
          minHeight: '80dvh',
        },
      }}
    >
      <Table store={store} excludedReservationIds={excludedReservationIds} />

      <Group
        pt="xs"
        pb="sm"
        justify="space-between"
        sx={(theme) => ({
          borderTop: `1px solid ${theme.colors.gray[2]}`,
        })}
      >
        <Selected store={store} />
        <SubmitButtons
          store={store}
          handleReservationIdSelect={handleReservationIdSelect}
          closeModal={closeModal}
          submitLabel={submitLabel}
        />
      </Group>
    </Modal>
  );
};

const SubmitButtons = ({
  store,
  handleReservationIdSelect,
  closeModal,
  submitLabel,
}: {
  store: StoreApi<StoreProps>;
  handleReservationIdSelect: Props['handleReservationIdSelect'];
  closeModal: () => void;
  submitLabel: Props['submitLabel'];
}) => {
  const [loading, setLoading] = useState(false);

  const id = useStore(store, (st) => st.reservation?.id);

  return (
    <Group>
      <Button type="reset" onClick={closeModal} disabled={loading}>
        Cancel
      </Button>
      <Button
        type="submit"
        variant="primary"
        loading={loading}
        disabled={!id}
        onClick={async () => {
          if (id) {
            setLoading(true);
            try {
              await handleReservationIdSelect(id);
              store.setState((e) => ({
                ...e,
                reservation: null,
                allPagesSelected: false,
                selected: {},
              }));
              closeModal();
            } catch (_error) {}

            setLoading(false);
          }
        }}
      >
        {submitLabel}
      </Button>
    </Group>
  );
};

const Selected = ({ store }: { store: StoreApi<StoreProps> }) => {
  const reservation = useStore(store, (st) => st.reservation);

  return (
    <Box>
      <Text size="xs" c="gray">
        Selected reservation:
      </Text>
      {!reservation ? (
        <Text fw={500} size="lg">
          -
        </Text>
      ) : (
        <Group flex={1} gap="md">
          <Avatar size="sm" src={reservation.connection?.app?.icon} />
          <Box
            sx={{
              textAlign: 'left',
            }}
          >
            <Text fw={500} size="sm" lh="xs">
              {reservation.guestName}
            </Text>
            <Text size="xs">{reservation.confirmationCode}</Text>
          </Box>
        </Group>
      )}
    </Box>
  );
};

const Table = ({
  store,
  excludedReservationIds,
}: {
  store: StoreApi<StoreProps>;
  excludedReservationIds: Props['excludedReservationIds'];
}) => {
  const rowSelection = useTableStoreSelected();
  const setRowSelection = useTableStoreSetSelected();

  const [sorting, setSorting] = useState<MRT_SortingState>([
    { id: 'checkIn', desc: true },
  ]);

  const setReservation = useStore(store, (st) => st.setReservation);
  const queryData = useReservationsTableQuery({
    excludeReservationIds: excludedReservationIds,
  });

  const { reset: resetFilter } = useReservationsTableFilter();

  const columns = useReservationsTableColumns({ hideTotal: false });

  const handleSelection = (selected: Record<string, boolean>) => {
    const oldSelection = Object.entries(rowSelection)
      .filter((x) => x[1])
      .map((x) => x[0]);
    const newSelection = Object.entries(selected)
      .filter((x) => x[1])
      .map((x) => x[0]);

    const newId = newSelection.find((x) => oldSelection.indexOf(x) === -1);

    if (!newSelection.length || !newId) {
      // boop
    } else {
      const allRows = queryData.data?.pages.flatMap((page) => page.list) || [];
      const selectedRow = allRows.find((row) => row.id === newId);

      if (selectedRow) {
        setRowSelection({ [newId]: true });
        setReservation({
          ...selectedRow,
          index: allRows.indexOf(selectedRow),
        });
      }
    }
  };

  return (
    <InfiniteTable
      queryData={queryData}
      columns={columns}
      resetFilter={resetFilter}
      table={{
        hideSettings: true,
        hidePagination: true,
        onRowClick: {
          handler: (row) => handleSelection({ [row.id]: true }),
        },
      }}
      sorting={{
        setSorting,
        sorting,
      }}
      selecting={{
        selectDisplayMode: 'radio',
        setAllPagesSelected: () => {},
        setRowSelection: (selected) => handleSelection(selected),
        rowSelection: { allPagesSelected: false, rows: rowSelection },
        disableSelectAll: true,
      }}
    >
      <ReservationsTableFilter hide={undefined} />
    </InfiniteTable>
  );
};
